import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { ApiSliceRootState } from '../types';
import { getApiBaseUrl } from '@/utils/apiBase';
import { normalizeProjectList } from '@/utils/projectList';
import { storage } from '@/utils/storage';
import { getTenantSlug } from '@/utils/tenant';
import { renewSessionTokens } from '../slices/authSlice';
import { API_CACHE_TAGS } from './apiTags';
import { refreshAccessToken } from '@/utils/tokenRefresh';
import { performSessionLogout } from '@/utils/sessionManager';

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const API_BASE_URL = getApiBaseUrl();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState, arg }) => {
    // Get token from Redux state or storage
    const token =
      (getState() as ApiSliceRootState).auth.accessToken || storage.getAccessToken();

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Add tenant context from subdomain
    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      headers.set('X-Tenant-Slug', tenantSlug);
    }

    // Multipart requests must not force application/json — the browser sets
    // multipart/form-data with the correct boundary (multer needs this).
    const body =
      arg && typeof arg === 'object' && 'body' in arg ? (arg as FetchArgs).body : undefined;
    if (body instanceof FormData) {
      return headers;
    }

    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

// Bare query (no Content-Type override) for multipart/form-data uploads
const baseQueryNoContentType = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token =
      (getState() as ApiSliceRootState).auth.accessToken || storage.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Add tenant context from subdomain
    const tenantSlug = getTenantSlug();
    if (tenantSlug) {
      headers.set('X-Tenant-Slug', tenantSlug);
    }

    // Intentionally no Content-Type – let the browser set multipart/form-data
    return headers;
  },
});

/**
 * Wrapper around rawBaseQuery that intercepts 401 responses.
 * On 401: silently attempts a token refresh. If refresh succeeds the original
 * request is retried transparently. If refresh fails (or no refresh token),
 * the user gets a toast warning and is logged out.
 */
const baseQuery: BaseQueryFn<string | (FetchArgs & { timeout?: number }), unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let requestArgs: string | FetchArgs = args;
  let abortTimer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;

  if (typeof args === 'object' && args && 'timeout' in args && typeof args.timeout === 'number') {
    const { timeout, ...rest } = args;
    controller = new AbortController();
    abortTimer = setTimeout(() => controller!.abort(), timeout);
    requestArgs = { ...rest, signal: controller.signal };
  }

  try {
    let result = await rawBaseQuery(requestArgs, api, extraOptions);

    if (result.error && result.error.status === 401) {
      // Skip refresh loop for the refresh endpoint itself to avoid recursion
      const url = typeof requestArgs === 'string' ? requestArgs : requestArgs?.url;
      if (url?.includes('/auth/refresh')) {
        await performSessionLogout(api.dispatch, 'expired');
        return result;
      }

      // If the user was never authenticated to begin with (e.g. landing on the
      // login page where some queries fire optimistically), do not show the
      // session-expired toast and do not dispatch logout — there is nothing to
      // expire. Just return the 401 silently so the caller can ignore it.
      const state = api.getState() as ApiSliceRootState;
      const hasAccess = !!state.auth.accessToken;
      const hasRefresh = !!storage.getRefreshToken();
      if (!hasAccess && !hasRefresh) {
        return result;
      }

      const refreshTkn = storage.getRefreshToken();
      if (refreshTkn) {
        const tokens = await refreshAccessToken();

        if (tokens?.accessToken) {
          api.dispatch(renewSessionTokens({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken || refreshTkn,
          }));
          api.dispatch(apiSlice.util.invalidateTags([...API_CACHE_TAGS]));
          result = await rawBaseQuery(requestArgs, api, extraOptions);
        } else {
          await performSessionLogout(api.dispatch, 'expired');
        }
      } else {
        await performSessionLogout(api.dispatch, 'expired');
      }
    }

    return result;
  } finally {
    if (abortTimer) clearTimeout(abortTimer);
  }
};

/**
 * RTK Query API Slice
 * Provides auto-generated hooks for API endpoints with caching
 */
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: [...API_CACHE_TAGS],
  endpoints: (builder) => ({
    // Task endpoints
    getTasks: builder.query({
      query: (params) => ({
        url: '/tasks',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Task'],
    }),
    /** Fetches every page for a project (backlog / full-tree views). */
    getAllProjectTasks: builder.query({
      async queryFn(
        params: {
          projectId: string;
          sprintId?: string;
          search?: string;
          sortBy?: string;
          sortOrder?: string;
        },
        _api,
        _extraOptions,
        baseQuery,
      ) {
        const pageSize = 500;
        let page = 1;
        let totalPages = 1;
        const merged: unknown[] = [];
        const seenIds = new Set<string>();

        while (page <= totalPages) {
          const result = await baseQuery({
            url: '/tasks',
            params: { ...params, page, limit: pageSize },
          });
          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }
          const body = result.data as ApiEnvelope<Array<{ id?: string }>>;
          for (const row of body.data ?? []) {
            const id = row?.id;
            if (id) {
              if (seenIds.has(id)) continue;
              seenIds.add(id);
            }
            merged.push(row);
          }
          totalPages = body.meta?.totalPages ?? 1;
          page += 1;
          if (page > 50) break;
        }

        return { data: merged };
      },
      providesTags: ['Task'],
    }),
    getTaskById: builder.query({
      query: (arg: string | { ref: string; type: string }) => {
        if (typeof arg === 'string') return `/tasks/${arg}`;
        return {
          url: `/tasks/${arg.ref}`,
          params: { type: arg.type },
        };
      },
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, arg) => [
        {
          type: 'Task',
          id: typeof arg === 'string' ? arg : arg.ref,
        },
      ],
    }),
    createTask: builder.mutation({
      query: (body) => ({
        url: '/tasks',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Task'],
    }),
    updateTask: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/tasks/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { id }) => [{ type: 'Task', id }, 'Task', { type: 'TaskHistory', id }],
    }),
    deleteTask: builder.mutation({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response?.data ?? null,
      invalidatesTags: ['Task'],
    }),
    uploadTaskAttachments: builder.mutation({
      queryFn: async ({ taskId, files }, api) => {
        const token = (api.getState() as ApiSliceRootState).auth.accessToken || storage.getAccessToken();
        const tenantSlug = getTenantSlug();

        const jsonHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) jsonHeaders['Authorization'] = `Bearer ${token}`;
        if (tenantSlug) jsonHeaders['X-Tenant-Slug'] = tenantSlug;

        const multipartHeaders: Record<string, string> = {};
        if (token) multipartHeaders['Authorization'] = `Bearer ${token}`;
        if (tenantSlug) multipartHeaders['X-Tenant-Slug'] = tenantSlug;

        const toFetchError = async (response: Response): Promise<FetchBaseQueryError> => {
          const errBody = await response.json().catch(() => ({ error: 'Upload failed' }));
          return { status: response.status, data: errBody };
        };

        let lastPayload: unknown = null;

        try {
          for (const file of files as File[]) {
            const initRes = await fetch(`${API_BASE_URL}/tasks/${taskId}/attachments/init`, {
              method: 'POST',
              headers: jsonHeaders,
              body: JSON.stringify({
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type || 'application/octet-stream',
              }),
            });

            if (!initRes.ok) {
              return { error: await toFetchError(initRes) };
            }

            const initEnvelope = (await initRes.json()) as ApiEnvelope<{
              method: 'azure-direct' | 'multipart';
              uploadUrl?: string;
              blobUrl?: string;
              uploadHeaders?: Record<string, string>;
            }>;
            const init = initEnvelope.data;

            if (init.method === 'azure-direct' && init.uploadUrl && init.blobUrl) {
              const putHeaders: Record<string, string> = {
                ...(init.uploadHeaders || {}),
                'Content-Type': file.type || init.uploadHeaders?.['Content-Type'] || 'application/octet-stream',
              };

              let directUploadOk = false;
              try {
                const putRes = await fetch(init.uploadUrl, {
                  method: 'PUT',
                  headers: putHeaders,
                  body: file,
                });
                directUploadOk = putRes.ok;
              } catch {
                // CSP, network, or CORS — fall back to API multipart below
                directUploadOk = false;
              }

              if (directUploadOk) {
                const completeRes = await fetch(`${API_BASE_URL}/tasks/${taskId}/attachments/complete`, {
                  method: 'POST',
                  headers: jsonHeaders,
                  body: JSON.stringify({
                    blobUrl: init.blobUrl,
                    fileSize: file.size,
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                  }),
                });

                if (!completeRes.ok) {
                  return { error: await toFetchError(completeRes) };
                }

                const completeJson = (await completeRes.json()) as ApiEnvelope<unknown>;
                lastPayload = completeJson.data;
                continue;
              }
              // Direct blob PUT failed — retry via multipart (same path as before May 18)
            }

            const formData = new FormData();
            formData.append('files', file);

            const postRes = await fetch(`${API_BASE_URL}/tasks/${taskId}/attachments`, {
              method: 'POST',
              headers: multipartHeaders,
              body: formData,
            });

            if (!postRes.ok) {
              return { error: await toFetchError(postRes) };
            }

            const postJson = (await postRes.json()) as ApiEnvelope<unknown>;
            lastPayload = postJson.data;
          }

          return { data: lastPayload };
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Upload failed';
          return { error: { status: 'FETCH_ERROR', error: message } as FetchBaseQueryError };
        }
      },
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }, 'Task'],
    }),
    deleteTaskAttachment: builder.mutation({
      query: ({ taskId, attachmentUrl }) => ({
        url: `/tasks/${taskId}/attachments`,
        method: 'DELETE',
        body: { attachmentUrl },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { taskId }) => [{ type: 'Task', id: taskId }, 'Task'],
    }),
    getTaskTree: builder.query({
      query: (params) => ({
        url: '/tasks/tree',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Task'],
    }),
    getTaskHistory: builder.query({
      query: ({ taskId, limit = 50, offset = 0, action, userId }) => ({
        url: `/tasks/${taskId}/history`,
        params: { limit, offset, action, userId },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, { taskId }) => [{ type: 'TaskHistory', id: taskId }],
    }),

    // Task watcher endpoints
    getTaskWatchers: builder.query({
      query: (taskId: string) => `/tasks/${taskId}/watchers`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, taskId) => [{ type: 'TaskWatcher', id: taskId }],
    }),
    toggleTaskWatch: builder.mutation({
      query: (taskId: string) => ({ url: `/tasks/${taskId}/watch`, method: 'POST' }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, taskId) => [{ type: 'TaskWatcher', id: taskId }],
    }),

    // Project endpoints
    getProjects: builder.query({
      query: (params) => ({
        url: '/projects',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => normalizeProjectList(response?.data),
      providesTags: ['Project'],
    }),
    getProjectById: builder.query({
      query: (id) => `/projects/${id}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, id) => [{ type: 'Project', id }],
    }),
    createProject: builder.mutation({
      query: (body) => ({
        url: '/projects',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Project'],
    }),
    updateProject: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/projects/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { id }) => [{ type: 'Project', id }, 'Project', 'ProjectMember'],
    }),
    getProjectMembers: builder.query({
      query: (projectId: string) => `/projects/${projectId}/members`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, projectId) => [{ type: 'ProjectMember', id: projectId }],
    }),
    getProjectAccess: builder.query({
      query: (arg: string | { projectId: string; includeDeleted?: boolean }) => {
        const projectId = typeof arg === 'string' ? arg : arg.projectId;
        const includeDeleted = typeof arg === 'string' ? undefined : arg.includeDeleted;
        return {
          url: `/projects/${projectId}/access`,
          params: includeDeleted ? { includeDeleted: 'true' } : undefined,
        };
      },
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, arg) => {
        const projectId = typeof arg === 'string' ? arg : arg.projectId;
        return [{ type: 'ProjectMember', id: projectId }];
      },
    }),
    addProjectTeamMember: builder.mutation({
      query: ({ projectId, teamId, userId, role }: { projectId: string; teamId: string; userId: string; role?: string }) => ({
        url: `/projects/${projectId}/teams/${teamId}/members`,
        method: 'POST',
        body: { userId, role },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }, 'Project', 'Economics'],
    }),
    removeProjectTeamMember: builder.mutation({
      query: ({ projectId, teamId, userId }: { projectId: string; teamId: string; userId: string }) => ({
        url: `/projects/${projectId}/teams/${teamId}/members/${userId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }, 'Project', 'Economics'],
    }),
    createProjectTeam: builder.mutation({
      query: ({ projectId, name, description }: { projectId: string; name: string; description?: string }) => ({
        url: `/projects/${projectId}/teams`,
        method: 'POST',
        body: { name, description },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }, 'Project', 'Economics'],
    }),
    deleteProjectTeam: builder.mutation({
      query: ({ projectId, teamId }: { projectId: string; teamId: string }) => ({
        url: `/projects/${projectId}/teams/${teamId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }, 'Project', 'Economics'],
    }),
    restoreProjectTeam: builder.mutation({
      query: ({ projectId, teamId }: { projectId: string; teamId: string }) => ({
        url: `/projects/${projectId}/teams/${teamId}/restore`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }, 'Project', 'Economics'],
    }),
    addProjectMember: builder.mutation({
      query: ({ projectId, userId, role }: { projectId: string; userId: string; role?: string }) => ({
        url: `/projects/${projectId}/members`,
        method: 'POST',
        body: { userId, role },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }, 'Economics'],
    }),
    updateProjectMember: builder.mutation({
      query: ({ projectId, userId, role }: { projectId: string; userId: string; role: string }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'PUT',
        body: { role },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }],
    }),
    removeProjectMember: builder.mutation({
      query: ({ projectId, userId }: { projectId: string; userId: string }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectMember', id: projectId }, 'Economics'],
    }),
    deleteProject: builder.mutation({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project'],
    }),
    restoreProject: builder.mutation({
      query: (id) => ({
        url: `/projects/${id}/restore`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Project'],
    }),

    // User endpoints
    getUsers: builder.query({
      query: (params) => ({
        url: '/users',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['User'],
    }),
    updateUser: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['User'],
    }),
    deleteUser: builder.mutation({
      query: (id: string) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    // Tenant endpoints
    getCurrentTenant: builder.query({
      query: () => '/tenants/current',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Tenant'],
    }),
    updateTenantSettings: builder.mutation({
      query: (body: { name?: string; settings?: Record<string, unknown> }) => ({
        url: '/tenants/settings',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Tenant'],
    }),
    uploadTenantLogo: builder.mutation<
      { logoUrl: string; settings: Record<string, unknown> },
      File
    >({
      queryFn: async (file, api) => {
        const formData = new FormData();
        formData.append('logo', file);
        const token = (api.getState() as ApiSliceRootState).auth.accessToken || storage.getAccessToken();
        const tenantSlug = getTenantSlug();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
        try {
          const response = await fetch(`${API_BASE_URL}/tenants/logo`, {
            method: 'POST',
            headers,
            body: formData,
          });
          const json = await response.json().catch(() => ({}));
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: json,
              } as FetchBaseQueryError,
            };
          }
          return { data: json.data as { logoUrl: string; settings: Record<string, unknown> } };
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Logo upload failed';
          return { error: { status: 'FETCH_ERROR', error: msg } as FetchBaseQueryError };
        }
      },
      invalidatesTags: ['Tenant'],
    }),

    getAuditLogs: builder.query<
      { logs: any[]; meta: { page: number; limit: number; total: number; totalPages?: number } },
      { page?: number; limit?: number; entity?: string; action?: string; startDate?: string; endDate?: string }
    >({
      query: (params) => ({
        url: '/audit',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any[]>) => ({
        logs: response.data ?? [],
        meta: response.meta ?? { page: 1, limit: 50, total: 0 },
      }),
    }),
    exportAuditLogs: builder.mutation<
      Blob,
      { format?: 'csv' | 'json'; entity?: string; action?: string; startDate?: string; endDate?: string }
    >({
      queryFn: async (params, api) => {
        const token = (api.getState() as ApiSliceRootState).auth.accessToken || storage.getAccessToken();
        const tenantSlug = getTenantSlug();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
        const qs = new URLSearchParams();
        if (params.format) qs.set('format', params.format);
        if (params.entity) qs.set('entity', params.entity);
        if (params.action) qs.set('action', params.action);
        if (params.startDate) qs.set('startDate', params.startDate);
        if (params.endDate) qs.set('endDate', params.endDate);
        try {
          const response = await fetch(`${API_BASE_URL}/audit/export?${qs.toString()}`, { headers });
          if (!response.ok) {
            const text = await response.text().catch(() => response.statusText);
            return {
              error: { status: response.status, data: { error: text } } as FetchBaseQueryError,
            };
          }
          const blob = await response.blob();
          return { data: blob };
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Export failed';
          return { error: { status: 'FETCH_ERROR', error: msg } as FetchBaseQueryError };
        }
      },
    }),

    updateProfile: builder.mutation({
      query: (body) => ({
        url: '/users/me',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['User'],
    }),

    // Team endpoints
    getTeams: builder.query({
      query: (params) => ({
        url: '/teams',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Team'],
    }),
    getTeamById: builder.query({
      query: (id) => `/teams/${id}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Team', 'User'],
    }),
    createTeam: builder.mutation({
      query: (body) => ({
        url: '/teams',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Team'],
    }),
    updateTeam: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/teams/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Team'],
    }),
    deleteTeam: builder.mutation({
      query: (id) => ({
        url: `/teams/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Team'],
    }),
    addTeamMember: builder.mutation({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/members`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Team', 'User'],
    }),
    removeTeamMember: builder.mutation({
      query: ({ teamId, userId }) => ({
        url: `/teams/${teamId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Team', 'User'],
    }),
    getTeamMembers: builder.query({
      query: ({ teamId, ...params }) => ({
        url: `/teams/${teamId}/members`,
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Team', 'User'],
    }),

    // Notification endpoints
    getNotifications: builder.query({
      query: (params) => ({
        url: '/notifications',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Notification'],
    }),
    markNotificationRead: builder.mutation({
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: 'PATCH',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Notification'],
    }),
    markAllNotificationsRead: builder.mutation({
      query: (ids) => ({
        url: '/notifications/read-all',
        method: 'PATCH',
        body: ids?.length ? { ids } : undefined,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Notification'],
    }),

    // Time Entry endpoints
    getTimeEntries: builder.query({
      query: (params) => ({
        url: '/time-tracking',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['TimeEntry'],
    }),
    createTimeEntry: builder.mutation({
      query: (body) => ({
        url: '/time-tracking',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['TimeEntry', 'Task'],
    }),
    startTimeEntry: builder.mutation({
      query: (body) => ({
        url: '/time-tracking/start',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['TimeEntry', 'Task'],
    }),
    stopTimeEntry: builder.mutation({
      query: (id) => ({
        url: `/time-tracking/${id}/stop`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['TimeEntry', 'Task'],
    }),
    deleteTimeEntry: builder.mutation({
      query: (id: string) => ({
        url: `/time-tracking/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TimeEntry', 'Task'],
    }),
    updateTimeEntry: builder.mutation({
      query: ({ id, ...body }: { id: string; startTime?: string; endTime?: string; description?: string }) => ({
        url: `/time-tracking/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['TimeEntry', 'Task'],
    }),
    getTimesheetWeek: builder.query({
      query: (params?: { weekStart?: string }) => ({
        url: '/timesheet/week',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['TimeEntry'],
    }),
    submitTimesheetWeek: builder.mutation({
      query: (body?: { weekStart?: string }) => ({
        url: '/timesheet/week/submit',
        method: 'POST',
        body: body || {},
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['TimeEntry'],
    }),
    getPendingTimesheets: builder.query({
      query: () => '/timesheet/pending',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['TimeEntry'],
    }),
    reviewTimesheetWeek: builder.mutation({
      query: ({ id, action, notes }: { id: string; action: 'approve' | 'reject'; notes?: string }) => ({
        url: `/timesheet/week/${id}/review`,
        method: 'POST',
        body: { action, notes },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['TimeEntry'],
    }),
    updateWorkPreferences: builder.mutation({
      query: (body: { weeklyCapacityHours: number }) => ({
        url: '/users/me/work-preferences',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['User'],
    }),

    // Comment endpoints
    getTaskComments: builder.query({
      query: (taskId) => `/tasks/${taskId}/comments`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Comment'],
    }),
    addComment: builder.mutation({
      query: ({ taskId, content }) => ({
        url: `/tasks/${taskId}/comments`,
        method: 'POST',
        body: { content },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Comment', 'Notification'],
    }),

    // Sprint endpoints
    getSprints: builder.query({
      query: (projectId) => ({
        url: '/sprints',
        params: projectId ? { projectId, limit: 100 } : { limit: 100 },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Sprint'],
    }),
    getActiveSprint: builder.query({
      query: (projectId) => ({
        url: '/sprints',
        params: {
          projectId,
          status: 'ACTIVE',
          limit: 1,
        },
      }),
      transformResponse: (response: ApiEnvelope<any>) =>
        Array.isArray(response.data) ? response.data[0] || null : response.data,
      providesTags: ['Sprint'],
    }),
    createSprint: builder.mutation({
      query: (body) => ({
        url: '/sprints',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Sprint', 'Task'],
    }),
    startSprint: builder.mutation({
      query: (id) => ({
        url: `/sprints/${id}/start`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Sprint', 'Task'],
    }),
    completeSprint: builder.mutation({
      query: (id) => ({
        url: `/sprints/${id}/complete`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Sprint', 'Task'],
    }),
    updateSprint: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/sprints/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Sprint'],
    }),
    deleteSprint: builder.mutation({
      query: (id) => ({
        url: `/sprints/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Sprint', 'Task'],
    }),

    // AI endpoints
    aiTaskSuggestion: builder.mutation({
      query: (body) => ({
        url: '/ai/task-suggestion',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiCreateTask: builder.mutation({
      query: (body) => ({
        url: '/ai/create-task',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Task'],
    }),
    aiGenerateDescription: builder.mutation({
      query: (body) => ({
        url: '/ai/generate-description',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiGenerateAcceptanceCriteria: builder.mutation({
      query: (body) => ({
        url: '/ai/generate-acceptance-criteria',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiGenerateBugDetails: builder.mutation({
      query: (body) => ({
        url: '/ai/generate-bug-details',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiExpandBullets: builder.mutation({
      query: (body) => ({
        url: '/ai/expand-bullets',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiSummarizeComments: builder.mutation({
      query: (body) => ({
        url: '/ai/summarize-comments',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiRewriteText: builder.mutation({
      query: (body) => ({
        url: '/ai/rewrite-text',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiSuggestFeature: builder.mutation({
      query: (body) => ({
        url: '/ai/suggest-feature',
        method: 'POST',
        body,
        // Feature plans can take longer on Azure OpenAI
        timeout: 120000,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    aiCreateFeature: builder.mutation({
      query: (body) => ({
        url: '/ai/create-feature',
        method: 'POST',
        body,
        timeout: 120000,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Task'],
    }),

    // Natural language report endpoint
    getNaturalLanguageReport: builder.mutation({
      query: (body) => ({
        url: '/reports/natural-language',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),

    getOverviewReport: builder.query({
      query: () => '/reports/overview',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),

    // Reports endpoints
    getProjectReport: builder.query({
      query: (projectId) => `/reports/project/${projectId}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getUserProductivity: builder.query({
      query: (userId) => `/reports/user/${userId}/productivity`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getVelocityReport: builder.query({
      query: (projectId) => `/reports/velocity/${projectId}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getWorkloadReport: builder.query({
      query: (params?: { projectId?: string }) => ({
        url: '/reports/workload',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getWorkloadTimeline: builder.query({
      query: (params: { projectId?: string; sprintId?: string; startDate?: string; endDate?: string } = {}) => ({
        url: '/reports/workload-timeline',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Task'],
    }),
    getTimeTrackingCalendar: builder.query({
      query: (params: { projectId?: string; sprintId?: string; startDate: string; endDate: string; timeZone?: string }) => ({
        url: '/reports/time-tracking-calendar',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['TimeEntry'],
    }),
    getTimeAllocationReport: builder.query({
      query: (params: {
        startDate: string;
        endDate: string;
        projectId?: string;
        sprintId?: string;
        userId?: string;
      }) => ({
        url: '/reports/time-allocation',
        params: {
          ...params,
          projectId: params.projectId && params.projectId !== 'all' ? params.projectId : undefined,
          userId: params.userId && params.userId !== 'all' ? params.userId : undefined,
        },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['TimeEntry'],
    }),
    getWorkLogExport: builder.query({
      query: (params: {
        projectId: string;
        startDate?: string;
        endDate?: string;
        sprintId?: string;
        taskIds?: string;
      }) => ({
        url: '/reports/work-log-export',
        params: {
          projectId: params.projectId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
          ...(params.sprintId ? { sprintId: params.sprintId } : {}),
          ...(params.taskIds ? { taskIds: params.taskIds } : {}),
        },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getSprintReport: builder.query({
      query: (sprintId) => `/reports/sprint/${sprintId}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getPlanToShipReport: builder.query({
      query: (sprintId: string) => `/reports/plan-to-ship/${sprintId}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    importTasks: builder.mutation<any, FormData>({
      queryFn: async (formData, { getState }) => {
        const state = getState() as ApiSliceRootState;
        const token = state.auth.accessToken || storage.getAccessToken();
        const baseUrl = API_BASE_URL;
        const tenantSlug = getTenantSlug();
        try {
          const response = await fetch(`${baseUrl}/tasks/import`, {
            method: 'POST',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}),
            },
            body: formData,
          });
          const json = await response.json();
          if (!response.ok) {
            return { error: { status: response.status, data: json } };
          }
          return { data: json.data ?? json };
        } catch (err: any) {
          return { error: { status: 'FETCH_ERROR', error: err.message } };
        }
      },
      invalidatesTags: ['Task'],
    }),
    // Tenant invitation
    inviteUserToTenant: builder.mutation({
      query: (body: { email: string; firstName?: string; lastName?: string; role?: string; password?: string }) => ({
        url: '/tenants/invite',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['User'],
    }),

    // Admin endpoints
    getAllTenants: builder.query({
      query: () => '/admin/tenants',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Tenant'],
    }),
    createTenantFromAdmin: builder.mutation({
      query: (body) => ({
        url: '/admin/tenants',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Tenant'],
    }),
    updateTenantFromAdmin: builder.mutation({
      query: ({ tenantId, ...body }) => ({
        url: `/admin/tenants/${tenantId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Tenant'],
    }),
    patchTenantStatusFromAdmin: builder.mutation({
      query: ({ tenantId, ...body }) => ({
        url: `/admin/tenants/${tenantId}/status`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Tenant'],
    }),
    softDeleteTenantFromAdmin: builder.mutation({
      query: ({ tenantId, ...body }: { tenantId: string; confirm: string; reason?: string }) => ({
        url: `/admin/tenants/${tenantId}`,
        method: 'DELETE',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Tenant'],
    }),
    restoreTenantFromAdmin: builder.mutation({
      query: (tenantId: string) => ({
        url: `/admin/tenants/${tenantId}/restore`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Tenant'],
    }),
    getAdminTenantEntitlements: builder.query({
      query: (tenantId: string) => `/admin/tenants/${tenantId}/entitlements`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_result, _err, tenantId) => [{ type: 'OrgModules', id: tenantId }],
    }),
    patchAdminTenantEntitlements: builder.mutation({
      query: ({
        tenantId,
        ...body
      }: {
        tenantId: string;
        planTemplateId?: string;
        products?: Record<string, { enabled?: boolean; status?: string; trialEndsAt?: string }>;
        moduleLimits?: Record<string, unknown>;
      }) => ({
        url: `/admin/tenants/${tenantId}/entitlements`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_result, _err, { tenantId }) => [
        'Tenant',
        'OrgModules',
        { type: 'OrgModules', id: tenantId },
        'OrgProducts',
      ],
    }),
    getAdminLicenses: builder.query({
      query: () => '/admin/licenses',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['AdminLicenses'],
    }),
    patchAdminLicense: builder.mutation({
      query: (body: {
        tenantId: string;
        userId: string;
        productId: string;
        assigned: boolean;
      }) => ({
        url: '/admin/licenses',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['AdminLicenses'],
    }),
    getIpAllowlist: builder.query({
      query: () => '/admin/ip-allowlist',
      transformResponse: (response: ApiEnvelope<{ cidrs: string[]; enforced: boolean }>) =>
        response.data,
    }),
    updateIpAllowlist: builder.mutation({
      query: (cidrs: string[]) => ({
        url: '/admin/ip-allowlist',
        method: 'PUT',
        body: { cidrs },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    clearIpAllowlist: builder.mutation({
      query: () => ({
        url: '/admin/ip-allowlist',
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getOrgApiKeys: builder.query({
      query: () => '/admin/api-keys',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    createOrgApiKey: builder.mutation({
      query: (body: { scopes: string[]; label?: string; ttlSeconds?: number }) => ({
        url: '/admin/api-keys',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    revokeOrgApiKey: builder.mutation({
      query: (keyId: string) => ({
        url: `/admin/api-keys/${keyId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    issueScimToken: builder.mutation({
      query: () => ({
        url: '/admin/scim/tokens',
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getAiUsageReport: builder.query({
      query: (params) => ({
        url: '/admin/ai-usage',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['AIUsage'],
    }),
    getTenantAiUsage: builder.query({
      query: ({ tenantId, ...params }) => ({
        url: `/admin/ai-usage/${tenantId}`,
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['AIUsage'],
    }),
    getPlatformAuditLogs: builder.query({
      query: (params) => ({
        url: '/admin/audit-logs',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getPlatformCatalog: builder.query({
      query: () => '/admin/platform-catalog',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Tenant'],
    }),
    patchPlatformCatalog: builder.mutation({
      query: (body) => ({
        url: '/admin/platform-catalog',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Tenant'],
    }),
    getMyAiUsage: builder.query({
      query: (params) => ({
        url: '/users/me/ai-usage',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['AIUsage'],
    }),

    // Feedback endpoints
    submitFeedback: builder.mutation({
      // Use the wrapped baseQuery (4th arg) so 401 → refresh matches other mutations.
      // FormData skips JSON Content-Type in rawBaseQuery.prepareHeaders.
      queryFn: async (formData, _api, _extraOptions, baseQueryWithReauth) => {
        const result = await baseQueryWithReauth({
          url: '/feedback',
          method: 'POST',
          body: formData,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: (result.data as ApiEnvelope<any>).data };
      },
      invalidatesTags: ['Feedback'],
    }),
    getFeedback: builder.query({
      query: (params) => ({
        url: '/feedback',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Feedback'],
    }),
    getFeedbackStats: builder.query({
      query: () => '/feedback/stats',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Feedback'],
    }),

    // Webhook endpoints
    getWebhooks: builder.query({
      query: () => '/webhooks',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Webhook'],
    }),
    createWebhook: builder.mutation({
      query: (body) => ({
        url: '/webhooks',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Webhook'],
    }),
    updateWebhook: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/webhooks/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Webhook'],
    }),
    deleteWebhook: builder.mutation({
      query: (id) => ({
        url: `/webhooks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Webhook'],
    }),
    testWebhook: builder.mutation({
      query: (id) => ({
        url: `/webhooks/${id}/test`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),

    // Custom Field endpoints
    getCustomFields: builder.query({
      query: (params) => ({
        url: '/custom-fields',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['CustomField'],
    }),
    createCustomField: builder.mutation({
      query: (body) => ({
        url: '/custom-fields',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['CustomField'],
    }),
    updateCustomField: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/custom-fields/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['CustomField'],
    }),
    deleteCustomField: builder.mutation({
      query: (id) => ({
        url: `/custom-fields/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomField'],
    }),
    getTaskCustomFieldValues: builder.query({
      query: (taskId) => `/custom-fields/values/${taskId}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['CustomField'],
    }),
    setTaskCustomFieldValues: builder.mutation({
      query: ({ taskId, values }) => ({
        url: `/custom-fields/values/${taskId}`,
        method: 'PUT',
        body: { values },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['CustomField'],
    }),

    // 2FA endpoints
    setup2FA: builder.mutation<any, void>({
      query: () => ({
        url: '/auth/2fa/setup',
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    verify2FA: builder.mutation({
      query: (body) => ({
        url: '/auth/2fa/verify',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    disable2FA: builder.mutation({
      query: (body) => ({
        url: '/auth/2fa/disable',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    validate2FA: builder.mutation({
      query: (body) => ({
        url: '/auth/2fa/validate',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),

    // Usage & Limits endpoints
    getUsageStats: builder.query({
      query: () => '/usage/stats',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Usage'],
    }),
    getPlanLimits: builder.query({
      query: () => '/usage/limits',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Usage'],
    }),
    getApproachingLimits: builder.query({
      query: () => '/usage/approaching-limits',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Usage'],
    }),

    // Project Economics (opt-in; no impact when disabled at tenant level)
    getEconomicsSettings: builder.query({
      query: () => '/economics/settings',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Economics'],
    }),
    updateEconomicsSettings: builder.mutation({
      query: (body: { enabled?: boolean; defaultCurrency?: string; defaultHourlyRate?: number }) => ({
        url: '/economics/settings',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Economics', 'Tenant'],
    }),
    getEconomicsPortfolio: builder.query({
      query: () => '/economics/portfolio',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Economics'],
    }),
    getProjectEconomicsProfile: builder.query({
      query: (projectId: string) => `/economics/projects/${projectId}/profile`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, projectId) => [{ type: 'Economics', id: projectId }],
    }),
    upsertProjectEconomicsProfile: builder.mutation({
      query: ({ projectId, ...body }: {
        projectId: string;
        budgetAmount: number;
        budgetCurrency?: string;
        defaultHourlyRate?: number;
        billingMode?: 'T_AND_M' | 'FIXED';
        clientName?: string;
        costOwnerId?: string | null;
        invoiceSurcharge?: {
          enabled: boolean;
          label: string;
          percentOfHours: number;
          ratePerHour: number;
        };
      }) => ({
        url: `/economics/projects/${projectId}/profile`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Economics', 'Project'],
    }),
    getProjectEconomicsStatement: builder.query({
      query: ({ projectId, year, month }: { projectId: string; year?: number; month?: number }) => ({
        url: `/economics/projects/${projectId}/statement`,
        params: { year, month },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, arg) => [{ type: 'Economics', id: `${arg.projectId}-stmt` }],
    }),
    getProjectMemberRates: builder.query({
      query: (projectId: string) => `/economics/projects/${projectId}/member-rates`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, projectId) => [{ type: 'Economics', id: `${projectId}-rates` }],
    }),
    upsertProjectMemberRates: builder.mutation({
      query: ({ projectId, memberRates }: {
        projectId: string;
        memberRates: Array<{
          userId: string;
          hourlyRate: number;
          bonus1PerHour?: number;
          bonus2PerHour?: number;
        }>;
      }) => ({
        url: `/economics/projects/${projectId}/member-rates`,
        method: 'PUT',
        body: { memberRates },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Economics', id: `${arg.projectId}-rates` },
        { type: 'Economics', id: `${arg.projectId}-labor` },
        { type: 'Economics', id: `${arg.projectId}-stmt` },
        'Economics',
      ],
    }),
    getProjectMemberLaborSummary: builder.query({
      query: ({
        projectId,
        periodType,
        year,
        month,
        sprintId,
      }: {
        projectId: string;
        periodType: 'month' | 'sprint';
        year?: number;
        month?: number;
        sprintId?: string;
      }) => ({
        url: `/economics/projects/${projectId}/member-labor`,
        params: { periodType, year, month, sprintId },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, arg) => [{ type: 'Economics', id: `${arg.projectId}-labor` }],
    }),
    getProjectReportHistory: builder.query({
      query: (projectId: string) => `/economics/projects/${projectId}/report-history`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, projectId) => [{ type: 'Economics', id: `${projectId}-history` }],
    }),
    getProjectYearlySummary: builder.query({
      query: ({ projectId, year }: { projectId: string; year?: number }) => ({
        url: `/economics/projects/${projectId}/yearly-summary`,
        params: { year },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, arg) => [{ type: 'Economics', id: `${arg.projectId}-yearly` }],
    }),
    updateProjectReportPayment: builder.mutation({
      query: ({
        projectId,
        reportId,
        ...body
      }: {
        projectId: string;
        reportId: string;
        paymentStatus: 'PENDING' | 'PAID' | 'PARTIAL';
        paidAmount?: number | null;
        paymentNotes?: string | null;
        invoiceRef?: string | null;
      }) => ({
        url: `/economics/projects/${projectId}/reports/${reportId}/payment`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Economics', id: `${arg.projectId}-history` },
        { type: 'Economics', id: `${arg.projectId}-yearly` },
        { type: 'Economics', id: `${arg.projectId}-labor` },
        'Economics',
      ],
    }),
    getLaborRates: builder.query({
      query: () => '/economics/rates',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Economics'],
    }),
    createLaborRate: builder.mutation({
      query: (body: {
        hourlyRate: number;
        userId?: string | null;
        projectId?: string | null;
        currency?: string;
        effectiveFrom?: string;
        effectiveTo?: string | null;
      }) => ({
        url: '/economics/rates',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Economics'],
    }),

    getVendorInvoicingSettings: builder.query({
      query: () => '/economics/vendor-invoicing/settings',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Economics'],
    }),
    updateVendorInvoicingSettings: builder.mutation({
      query: (body: {
        enabled?: boolean;
        bonusesEnabled?: boolean;
        bonus1Label?: string;
        bonus2Label?: string;
      }) => ({
        url: '/economics/vendor-invoicing/settings',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Economics', 'Tenant'],
    }),
    getProjectVendorInvoices: builder.query({
      query: ({ projectId, status }: { projectId: string; status?: string }) => ({
        url: `/economics/projects/${projectId}/invoices`,
        params: status ? { status } : undefined,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, arg) => [{ type: 'Economics', id: `${arg.projectId}-invoices` }],
    }),
    getProjectVendorInvoiceQueue: builder.query({
      query: (projectId: string) => `/economics/projects/${projectId}/invoices/queue`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, projectId) => [{ type: 'Economics', id: `${projectId}-invoice-queue` }],
    }),
    getProjectVendorInvoice: builder.query({
      query: ({ projectId, invoiceId }: { projectId: string; invoiceId: string }) =>
        `/economics/projects/${projectId}/invoices/${invoiceId}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (_r, _e, arg) => [{ type: 'Economics', id: `invoice-${arg.invoiceId}` }],
    }),
    generateProjectVendorInvoice: builder.mutation({
      query: ({
        projectId,
        ...body
      }: {
        projectId: string;
        periodType?: 'month' | 'sprint';
        year?: number;
        month?: number;
        sprintId?: string;
      }) => ({
        url: `/economics/projects/${projectId}/invoices`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Economics', id: `${arg.projectId}-invoices` },
        { type: 'Economics', id: `${arg.projectId}-invoice-queue` },
        { type: 'Economics', id: `${arg.projectId}-labor` },
      ],
    }),
    deleteProjectVendorInvoice: builder.mutation({
      query: ({ projectId, invoiceId }: { projectId: string; invoiceId: string }) => ({
        url: `/economics/projects/${projectId}/invoices/${invoiceId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Economics', id: `${arg.projectId}-invoices` },
        { type: 'Economics', id: `invoice-${arg.invoiceId}` },
        { type: 'Economics', id: `${arg.projectId}-invoice-queue` },
      ],
    }),
    patchProjectVendorInvoice: builder.mutation({
      query: ({
        projectId,
        invoiceId,
        ...body
      }: {
        projectId: string;
        invoiceId: string;
        bonus1Applied?: boolean;
        bonus2Applied?: boolean;
      }) => ({
        url: `/economics/projects/${projectId}/invoices/${invoiceId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Economics', id: `${arg.projectId}-invoices` },
        { type: 'Economics', id: `invoice-${arg.invoiceId}` },
        { type: 'Economics', id: `${arg.projectId}-invoice-queue` },
      ],
    }),
    approveProjectVendorInvoice: builder.mutation({
      query: ({ projectId, invoiceId }: { projectId: string; invoiceId: string }) => ({
        url: `/economics/projects/${projectId}/invoices/${invoiceId}/approve`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Economics', id: `${arg.projectId}-invoices` },
        { type: 'Economics', id: `invoice-${arg.invoiceId}` },
        { type: 'Economics', id: `${arg.projectId}-invoice-queue` },
        { type: 'Economics', id: `${arg.projectId}-rates` },
      ],
    }),
    markProjectVendorInvoicePaid: builder.mutation({
      query: ({
        projectId,
        invoiceId,
        ...body
      }: {
        projectId: string;
        invoiceId: string;
        paidAmount?: number | null;
        paymentNotes?: string | null;
        invoiceRef?: string | null;
      }) => ({
        url: `/economics/projects/${projectId}/invoices/${invoiceId}/payment`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Economics', id: `${arg.projectId}-invoices` },
        { type: 'Economics', id: `invoice-${arg.invoiceId}` },
        { type: 'Economics', id: `${arg.projectId}-invoice-queue` },
        { type: 'Economics', id: `${arg.projectId}-yearly` },
        { type: 'Economics', id: `${arg.projectId}-rates` },
      ],
    }),

    // Billing endpoints
    createPortalSession: builder.mutation({
      query: () => ({
        url: '/billing/portal',
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<{ url?: string; portalUrl?: string; inAppOnly?: boolean; message?: string }>) => {
        const data = response.data ?? {};
        return {
          portalUrl: data.portalUrl ?? data.url ?? '',
          inAppOnly: !!data.inAppOnly,
          message: data.message ?? '',
        };
      },
    }),
    getSubscription: builder.query({
      query: () => '/billing/subscription',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Billing'],
    }),
    contactSales: builder.mutation<{ message: string; emailDelivered?: boolean }, { message: string }>({
      query: (body) => ({
        url: '/billing/contact',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<{ emailDelivered?: boolean }>) => ({
        message: response.message || 'Sent',
        emailDelivered: response.data?.emailDelivered,
      }),
    }),
    createAidlcCheckout: builder.mutation({
      query: (body: {
        interval?: string;
        products: { productId: string; enabled: boolean; seats?: number }[];
      }) => ({
        url: '/billing/checkout-aidlc',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<Record<string, unknown>>) => response.data,
      invalidatesTags: ['Billing', 'OrgProducts'],
    }),
    createRazorpayOrder: builder.mutation<
      { order_id: string; amount: number; currency: string; key_id: string },
      { amount: number; currency?: string; receipt?: string }
    >({
      query: (body) => ({
        url: '/payments/create-order',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<{ order_id: string; amount: number; currency: string; key_id: string }>) =>
        response.data,
    }),
    verifyRazorpayPayment: builder.mutation<
      { verified: boolean; orderId: string; paymentId: string },
      { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }
    >({
      query: (body) => ({
        url: '/payments/verify-payment',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<{ verified: boolean; orderId: string; paymentId: string }>) =>
        response.data,
      invalidatesTags: ['Billing', 'OrgProducts'],
    }),
    getBillingInvoices: builder.query({
      query: () => '/billing/invoices',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Billing'],
    }),
    getBillingTaxProfile: builder.query({
      query: () => '/billing/tax-profile',
      transformResponse: (response: ApiEnvelope<{ profile: any }>) => response.data,
      providesTags: ['Billing'],
    }),
    updateBillingTaxProfile: builder.mutation({
      query: (body: Record<string, unknown>) => ({
        url: '/billing/tax-profile',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<{ profile: any }>) => response.data,
      invalidatesTags: ['Billing'],
    }),

    getOrgProducts: builder.query({
      query: () => '/org/products',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['OrgProducts'],
    }),
    getOrgModules: builder.query({
      query: () => '/org/modules',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['OrgModules'],
    }),

    getCodeGitHub: builder.query({
      query: () => '/code/github',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Code'],
    }),
    getCodeRepos: builder.query({
      query: () => '/code/repos',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Code'],
    }),
    getCodeScans: builder.query({
      query: (params?: { limit?: number }) => ({
        url: '/code/scans',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Code'],
    }),
    patchCodeRepoSelection: builder.mutation({
      query: (body: { repos: Array<{ id: number; owner: string; name: string; fullName: string }> }) => ({
        url: '/code/repos/selection',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Code'],
    }),
    postCodeRepoScan: builder.mutation({
      query: ({ owner, repo }: { owner: string; repo: string }) => ({
        url: `/code/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/scans`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Code'],
    }),

    patchOrgProducts: builder.mutation({
      query: (body: { products: Record<string, { enabled?: boolean; status?: string }> }) => ({
        url: '/org/products',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['OrgProducts', 'Tenant'],
    }),

    getMyProductLicenses: builder.query({
      query: () => '/org/my-licenses',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['MyProductLicenses'],
    }),

    getOrgProductAccess: builder.query({
      query: () => '/org/product-access',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['ProductAccess', 'Billing'],
    }),
    patchOrgProductAccess: builder.mutation({
      query: (body: { userId: string; productId: string; assigned: boolean }) => ({
        url: '/org/product-access',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['ProductAccess', 'Billing', 'Workspace', 'MyProductLicenses'],
    }),
    getOrgAiUsage: builder.query({
      query: (params?: { startDate?: string; endDate?: string }) => ({
        url: '/org/ai-usage',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['AIUsage'],
    }),

    getWorkspaceContext: builder.query({
      query: () => '/workspace/context',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Workspace'],
    }),
    patchWorkspaceDefaults: builder.mutation({
      query: (body: { landingProductId?: string | null }) => ({
        url: '/workspace/defaults',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Workspace', 'Tenant'],
    }),
    discoverTenantForLogin: builder.mutation({
      query: (body: { email: string }) => ({
        url: '/auth/discover-tenant',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<{ tenants: any[] }>) => response.data,
    }),

    getCostEvents: builder.query({
      query: (params?: { limit?: number; sourceProduct?: string }) => ({
        url: '/cost/events',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Cost'],
    }),
    getCostSummary: builder.query({
      query: () => '/cost/summary',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Cost'],
    }),

    // Waitlist endpoints
    submitWaitlist: builder.mutation({
      query: (body) => ({
        url: '/waitlist/signup',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getWaitlistEntries: builder.query({
      query: (params?: { status?: string; search?: string }) => ({
        url: '/waitlist',
        params,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Waitlist'],
    }),
    getWaitlistStats: builder.query({
      query: () => '/waitlist/stats',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Waitlist'],
    }),
    approveWaitlist: builder.mutation({
      query: (id: string) => ({
        url: `/waitlist/${id}/approve`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Waitlist'],
    }),
    rejectWaitlist: builder.mutation({
      query: ({ id, reason }: { id: string; reason?: string }) => ({
        url: `/waitlist/${id}/reject`,
        method: 'POST',
        body: { reason },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Waitlist'],
    }),
    updateWaitlistNotes: builder.mutation({
      query: ({ id, notes }: { id: string; notes: string }) => ({
        url: `/waitlist/${id}/notes`,
        method: 'PATCH',
        body: { notes },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Waitlist'],
    }),
    resendWaitlistInvite: builder.mutation({
      query: (id: string) => ({
        url: `/waitlist/${id}/resend`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Waitlist'],
    }),
    validateInviteCode: builder.query({
      query: (inviteCode: string) => `/waitlist/validate/${inviteCode}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    
    // Search
    search: builder.query({
      query: ({ q, limit = 5 }: { q: string; limit?: number }) => `/search?q=${encodeURIComponent(q)}&limit=${limit}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),

    // Canvas endpoints
    getCanvases: builder.query({
      query: () => '/canvases',
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: ['Canvas'],
    }),
    getCanvasById: builder.query({
      query: (id: string) => `/canvases/${id}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, id) => [{ type: 'Canvas', id }],
    }),
    getSharedCanvas: builder.query({
      query: (token: string) => `/canvases/shared/${token}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    createCanvas: builder.mutation({
      query: (body) => ({
        url: '/canvases',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Canvas'],
    }),
    updateCanvas: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/canvases/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { id }) => [{ type: 'Canvas', id }, 'Canvas'],
    }),
    deleteCanvas: builder.mutation({
      query: (id: string) => ({
        url: `/canvases/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Canvas'],
    }),
    shareCanvas: builder.mutation({
      query: (id: string) => ({
        url: `/canvases/${id}/share`,
        method: 'POST',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, id) => [{ type: 'Canvas', id }],
    }),
    unshareCanvas: builder.mutation({
      query: (id: string) => ({
        url: `/canvases/${id}/share`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, id) => [{ type: 'Canvas', id }],
    }),

    // Dependency endpoints
    getDependencies: builder.query<any[], string>({
      query: (taskId: string) => `/dependencies?taskId=${taskId}`,
      transformResponse: (response: ApiEnvelope<any[]>) => response.data,
      providesTags: (result, error, taskId) => [
        { type: 'Dependency', id: taskId },
        'Dependency',
      ],
    }),
    createDependency: builder.mutation<any, { fromTaskId: string; toTaskId: string; type: string }>({
      query: (body) => ({
        url: '/dependencies',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { fromTaskId, toTaskId }) => [
        { type: 'Dependency', id: fromTaskId },
        { type: 'Dependency', id: toTaskId },
        'Dependency',
      ],
    }),
    deleteDependency: builder.mutation<any, string>({
      query: (id: string) => ({
        url: `/dependencies/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Dependency'],
    }),

    // Milestone endpoints
    getMilestones: builder.query<any[], { projectId?: string } | void>({
      query: (params) => ({
        url: '/milestones',
        params: params || {},
      }),
      transformResponse: (response: ApiEnvelope<any[]>) => response.data,
      providesTags: ['Milestone'],
    }),
    getMilestoneById: builder.query<any, string>({
      query: (id: string) => `/milestones/${id}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, id) => [{ type: 'Milestone', id }],
    }),
    createMilestone: builder.mutation<any, { projectId: string; title: string; description?: string; dueDate?: string; status?: string }>({
      query: (body) => ({
        url: '/milestones',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Milestone'],
    }),
    updateMilestone: builder.mutation<any, { id: string; title?: string; description?: string; dueDate?: string; status?: string }>({
      query: ({ id, ...body }) => ({
        url: `/milestones/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, { id }) => [{ type: 'Milestone', id }, 'Milestone'],
    }),
    deleteMilestone: builder.mutation<any, string>({
      query: (id: string) => ({
        url: `/milestones/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Milestone'],
    }),

    // Personal follow-up endpoints
    getFollowUps: builder.query<any[], void>({
      query: () => '/follow-ups',
      transformResponse: (response: ApiEnvelope<any[]>) => response.data ?? [],
      providesTags: ['FollowUp'],
    }),
    parseFollowUp: builder.mutation<any, { text: string }>({
      query: (body) => ({
        url: '/follow-ups/parse',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    createFollowUp: builder.mutation<
      any,
      { title: string; note?: string; dueDate?: string; waitingOnUserId?: string; waitingOnName?: string }
    >({
      query: (body) => ({
        url: '/follow-ups',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['FollowUp'],
    }),
    completeFollowUp: builder.mutation<any, string>({
      query: (id) => ({
        url: `/follow-ups/${id}/complete`,
        method: 'PATCH',
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['FollowUp'],
    }),
    postponeFollowUp: builder.mutation<
      any,
      { id: string; preset?: string; postponeTo?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/follow-ups/${id}/postpone`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['FollowUp'],
    }),
    deleteFollowUp: builder.mutation<void, string>({
      query: (id) => ({
        url: `/follow-ups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FollowUp'],
    }),

    // Bulk task operations
    bulkUpdateTasks: builder.mutation<{ updatedCount: number }, { ids: string[]; updates: Record<string, any> }>({
      query: (body) => ({
        url: '/tasks/bulk',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Task'],
    }),
    bulkDeleteTasks: builder.mutation<{ deletedCount: number }, { ids: string[] }>({
      query: (body) => ({
        url: '/tasks/bulk',
        method: 'DELETE',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['Task'],
    }),

    // Lead/cycle time metrics
    getLeadCycleMetrics: builder.query<any, { projectId?: string; days?: number } | void>({
      query: (params) => ({
        url: '/tasks/metrics/lead-cycle',
        params: params || {},
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),

    // Project wiki (see backend wiki.routes)
    getWikiSpaceForProject: builder.query<any, string>({
      query: (projectId) => `/wiki/spaces/project/${projectId}`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, projectId) => [{ type: 'Wiki', id: `SPACE-${projectId}` }],
    }),
    getWikiPages: builder.query<
      any,
      {
        projectId?: string;
        status?: string;
        parentId?: string | null;
        search?: string;
        pageType?: string;
        /** When true, requires `search` (≥2 chars) and ignores `projectId` — handbook search across all accessible project wikis. */
        tenantWideSearch?: boolean;
      }
    >({
      query: (arg) => {
        const { tenantWideSearch, projectId, ...rest } = arg;
        if (tenantWideSearch) {
          return {
            url: '/wiki/pages',
            params: { tenantWide: 'true', search: rest.search },
          };
        }
        return {
          url: '/wiki/pages',
          params: { projectId, ...rest },
        };
      },
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, arg) =>
        arg.tenantWideSearch
          ? [{ type: 'Wiki' as const, id: 'TENANT-SEARCH' }]
          : [
              { type: 'Wiki' as const, id: `LIST-${String(arg.projectId)}` },
              { type: 'Wiki' as const, id: `TREE-${String(arg.projectId)}` },
            ],
    }),
    getWikiTree: builder.query<any, string>({
      query: (projectId) => `/wiki/spaces/project/${projectId}/tree`,
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, projectId) => [{ type: 'Wiki', id: `TREE-${projectId}` }],
    }),
    getWikiFreshness: builder.query<any, string>({
      query: (projectId) => ({ url: '/wiki/freshness', params: { projectId } }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, projectId) => [{ type: 'Wiki', id: `FRESH-${projectId}` }],
    }),
    getWikiRevisions: builder.query<any, { pageId: string; projectId: string }>({
      query: ({ pageId, projectId }) => ({
        url: `/wiki/pages/${pageId}/revisions`,
        params: { projectId },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    getWikiPage: builder.query<any, { id: string; projectId: string; incrementView?: boolean }>({
      query: ({ id, projectId, incrementView }) => ({
        url: `/wiki/pages/${id}`,
        params: {
          projectId,
          ...(incrementView === false ? { incrementView: 'false' } : {}),
        },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, { id }) => [{ type: 'Wiki', id }],
    }),
    getWikiPageWatchers: builder.query<
      {
        watchers: {
          id: string;
          firstName: string;
          lastName: string;
          avatar?: string | null;
          email: string;
        }[];
        isWatching: boolean;
      },
      { pageId: string; projectId: string }
    >({
      query: ({ pageId, projectId }) => ({
        url: `/wiki/pages/${pageId}/watchers`,
        params: { projectId },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      providesTags: (result, error, arg) => [{ type: 'Wiki', id: `WATCH-${arg.pageId}` }],
    }),
    toggleWikiPageWatch: builder.mutation<{ isWatching: boolean }, { pageId: string; projectId: string }>({
      query: ({ pageId, projectId }) => ({
        url: `/wiki/pages/${pageId}/watch`,
        method: 'POST',
        params: { projectId },
      }),
      transformResponse: (response: ApiEnvelope<{ isWatching: boolean }>) => response.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Wiki', id: `WATCH-${arg.pageId}` }],
    }),
    createWikiPage: builder.mutation<any, Record<string, unknown>>({
      query: (body) => ({ url: '/wiki/pages', method: 'POST', body }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, arg) => {
        const pid = arg?.projectId as string | undefined;
        return pid
          ? [
              { type: 'Wiki', id: `LIST-${pid}` },
              { type: 'Wiki', id: `SPACE-${pid}` },
              { type: 'Wiki', id: `TREE-${pid}` },
            ]
          : ['Wiki'];
      },
    }),
    updateWikiPage: builder.mutation<any, { id: string; projectId: string; [key: string]: unknown }>({
      query: ({ id, projectId, ...body }) => ({
        url: `/wiki/pages/${id}`,
        method: 'PATCH',
        body: { projectId, ...body },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, arg) => [
        { type: 'Wiki', id: arg.id },
        { type: 'Wiki', id: `LIST-${arg.projectId}` },
        { type: 'Wiki', id: `SPACE-${arg.projectId}` },
        { type: 'Wiki', id: `TREE-${arg.projectId}` },
        { type: 'Wiki', id: `FRESH-${arg.projectId}` },
        { type: 'Wiki', id: 'TENANT-SEARCH' },
        'Wiki',
      ],
    }),
    archiveWikiPage: builder.mutation<any, { id: string; projectId: string }>({
      query: ({ id, projectId }) => ({
        url: `/wiki/pages/${id}`,
        method: 'DELETE',
        params: { projectId },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, arg) => [
        { type: 'Wiki', id: arg.id },
        { type: 'Wiki', id: `LIST-${arg.projectId}` },
        { type: 'Wiki', id: `SPACE-${arg.projectId}` },
        { type: 'Wiki', id: `TREE-${arg.projectId}` },
      ],
    }),
    revertWikiRevision: builder.mutation<
      any,
      { pageId: string; projectId: string; version: number }
    >({
      query: ({ pageId, projectId, version }) => ({
        url: `/wiki/pages/${pageId}/revert`,
        method: 'POST',
        body: { projectId, version },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, arg) => [
        { type: 'Wiki', id: arg.pageId },
        { type: 'Wiki', id: `LIST-${arg.projectId}` },
        { type: 'Wiki', id: `TREE-${arg.projectId}` },
      ],
    }),
    wikiSummarizePage: builder.mutation<any, { pageId: string; projectId: string }>({
      query: ({ pageId, projectId }) => ({
        url: `/wiki/pages/${pageId}/ai-summarize`,
        method: 'POST',
        body: { projectId },
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Wiki', id: arg.pageId }],
    }),
    wikiAiDraft: builder.mutation<
      any,
      { projectId: string; prompt: string; templateHint?: string }
    >({
      query: (body) => ({ url: '/wiki/ai-draft', method: 'POST', body }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),
    wikiExport: builder.mutation<
      Blob,
      {
        projectId: string;
        scope: 'page' | 'subtree' | 'space';
        pageId?: string | null;
        format: 'pdf' | 'docx';
      }
    >({
      queryFn: async (arg, api) => {
        const token = (api.getState() as ApiSliceRootState).auth.accessToken || storage.getAccessToken();
        const tenantSlug = getTenantSlug();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
        try {
          const response = await fetch(`${API_BASE_URL}/wiki/export`, {
            method: 'POST',
            headers,
            body: JSON.stringify(arg),
          });
          if (!response.ok) {
            const text = await response.text().catch(() => response.statusText);
            return {
              error: { status: response.status, data: { error: text } } as FetchBaseQueryError,
            };
          }
          const blob = await response.blob();
          return { data: blob };
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Export failed';
          return { error: { status: 'FETCH_ERROR', error: msg } as FetchBaseQueryError };
        }
      },
    }),
    uploadWikiAttachment: builder.mutation<
      { url: string; displayUrl?: string },
      { pageId: string; projectId: string; file: File }
    >({
      queryFn: async ({ pageId, projectId, file }, api) => {
        const formData = new FormData();
        formData.append('projectId', projectId);
        formData.append('file', file);
        const token = (api.getState() as ApiSliceRootState).auth.accessToken || storage.getAccessToken();
        const tenantSlug = getTenantSlug();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
        try {
          const response = await fetch(`${API_BASE_URL}/wiki/pages/${pageId}/attachments`, {
            method: 'POST',
            headers,
            body: formData,
          });
          if (!response.ok) {
            const errBody = await response.json().catch(() => ({ message: 'Upload failed' }));
            return { error: { status: response.status, data: errBody } as FetchBaseQueryError };
          }
          const json = await response.json();
          return { data: (json as ApiEnvelope<{ url: string; displayUrl?: string }>).data };
        } catch (err: any) {
          return { error: { status: 'FETCH_ERROR', error: err?.message ?? 'Upload failed' } as FetchBaseQueryError };
        }
      },
    }),
    askWikiQuestion: builder.mutation<
      any,
      { projectId: string; question: string; includeDrafts?: boolean }
    >({
      query: (body) => ({ url: '/wiki/ask', method: 'POST', body }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
    }),

    // Update locale/timezone preferences
    updateLocalePreferences: builder.mutation<any, { locale?: string; timezone?: string; dateFormat?: string }>({
      query: (body) => ({
        url: '/users/me/locale',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApiEnvelope<any>) => response.data,
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetAllProjectTasksQuery,
  useGetTaskByIdQuery,
  useGetTaskTreeQuery,
  useGetTaskHistoryQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useUploadTaskAttachmentsMutation,
  useDeleteTaskAttachmentMutation,
  useGetTaskWatchersQuery,
  useToggleTaskWatchMutation,
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useRestoreProjectMutation,
  useGetProjectMembersQuery,
  useGetProjectAccessQuery,
  useAddProjectMemberMutation,
  useAddProjectTeamMemberMutation,
  useUpdateProjectMemberMutation,
  useRemoveProjectMemberMutation,
  useRemoveProjectTeamMemberMutation,
  useCreateProjectTeamMutation,
  useDeleteProjectTeamMutation,
  useRestoreProjectTeamMutation,
  useGetUsersQuery,
  useUpdateUserMutation,
  useGetCurrentTenantQuery,
  useUpdateTenantSettingsMutation,
  useUploadTenantLogoMutation,
  useGetAuditLogsQuery,
  useExportAuditLogsMutation,
  useUpdateProfileMutation,
  useGetTeamsQuery,
  useGetTeamByIdQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useAddTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useGetTeamMembersQuery,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetTimeEntriesQuery,
  useCreateTimeEntryMutation,
  useStartTimeEntryMutation,
  useStopTimeEntryMutation,
  useDeleteTimeEntryMutation,
  useUpdateTimeEntryMutation,
  useGetTimesheetWeekQuery,
  useSubmitTimesheetWeekMutation,
  useGetPendingTimesheetsQuery,
  useReviewTimesheetWeekMutation,
  useUpdateWorkPreferencesMutation,
  useGetTaskCommentsQuery,
  useAddCommentMutation,
  useGetSprintsQuery,
  useGetActiveSprintQuery,
  useCreateSprintMutation,
  useStartSprintMutation,
  useCompleteSprintMutation,
  useUpdateSprintMutation,
  useDeleteSprintMutation,
  useAiTaskSuggestionMutation,
  useAiCreateTaskMutation,
  useAiGenerateDescriptionMutation,
  useAiGenerateAcceptanceCriteriaMutation,
  useAiGenerateBugDetailsMutation,
  useAiExpandBulletsMutation,
  useAiSummarizeCommentsMutation,
  useAiRewriteTextMutation,
  useAiSuggestFeatureMutation,
  useAiCreateFeatureMutation,
  useGetProjectReportQuery,
  useGetUserProductivityQuery,
  useGetNaturalLanguageReportMutation,
  useGetOverviewReportQuery,
  useGetVelocityReportQuery,
  useGetWorkloadReportQuery,
  useGetWorkloadTimelineQuery,
  useGetTimeTrackingCalendarQuery,
  useGetWorkLogExportQuery,
  useLazyGetWorkLogExportQuery,
  useGetTimeAllocationReportQuery,
  useGetSprintReportQuery,
  useGetPlanToShipReportQuery,
  useImportTasksMutation,
  useInviteUserToTenantMutation,
  useDeleteUserMutation,
  useGetAllTenantsQuery,
  useCreateTenantFromAdminMutation,
  useUpdateTenantFromAdminMutation,
  usePatchTenantStatusFromAdminMutation,
  useSoftDeleteTenantFromAdminMutation,
  useRestoreTenantFromAdminMutation,
  useGetAdminTenantEntitlementsQuery,
  usePatchAdminTenantEntitlementsMutation,
  useGetAdminLicensesQuery,
  usePatchAdminLicenseMutation,
  useGetIpAllowlistQuery,
  useUpdateIpAllowlistMutation,
  useClearIpAllowlistMutation,
  useGetOrgApiKeysQuery,
  useCreateOrgApiKeyMutation,
  useRevokeOrgApiKeyMutation,
  useIssueScimTokenMutation,
  useGetAiUsageReportQuery,
  useGetTenantAiUsageQuery,
  useGetPlatformAuditLogsQuery,
  useGetPlatformCatalogQuery,
  usePatchPlatformCatalogMutation,
  useGetMyAiUsageQuery,
  useSubmitFeedbackMutation,
  useGetFeedbackQuery,
  useGetFeedbackStatsQuery,
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useTestWebhookMutation,
  useGetCustomFieldsQuery,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useDeleteCustomFieldMutation,
  useGetTaskCustomFieldValuesQuery,
  useSetTaskCustomFieldValuesMutation,
  useSetup2FAMutation,
  useVerify2FAMutation,
  useDisable2FAMutation,
  useValidate2FAMutation,
  useGetUsageStatsQuery,
  useGetPlanLimitsQuery,
  useGetApproachingLimitsQuery,
  useGetEconomicsSettingsQuery,
  useUpdateEconomicsSettingsMutation,
  useGetEconomicsPortfolioQuery,
  useGetProjectEconomicsProfileQuery,
  useUpsertProjectEconomicsProfileMutation,
  useGetProjectEconomicsStatementQuery,
  useGetProjectMemberRatesQuery,
  useUpsertProjectMemberRatesMutation,
  useGetProjectMemberLaborSummaryQuery,
  useGetProjectReportHistoryQuery,
  useGetProjectYearlySummaryQuery,
  useUpdateProjectReportPaymentMutation,
  useGetLaborRatesQuery,
  useCreateLaborRateMutation,
  useGetVendorInvoicingSettingsQuery,
  useUpdateVendorInvoicingSettingsMutation,
  useGetProjectVendorInvoicesQuery,
  useGetProjectVendorInvoiceQueueQuery,
  useGetProjectVendorInvoiceQuery,
  useGenerateProjectVendorInvoiceMutation,
  useDeleteProjectVendorInvoiceMutation,
  usePatchProjectVendorInvoiceMutation,
  useApproveProjectVendorInvoiceMutation,
  useMarkProjectVendorInvoicePaidMutation,
  useCreatePortalSessionMutation,
  useGetSubscriptionQuery,
  useContactSalesMutation,
  useCreateAidlcCheckoutMutation,
  useCreateRazorpayOrderMutation,
  useVerifyRazorpayPaymentMutation,
  useGetBillingInvoicesQuery,
  useGetBillingTaxProfileQuery,
  useUpdateBillingTaxProfileMutation,
  useGetOrgProductsQuery,
  useGetOrgModulesQuery,
  useGetCodeGitHubQuery,
  useGetCodeReposQuery,
  useGetCodeScansQuery,
  usePatchCodeRepoSelectionMutation,
  usePostCodeRepoScanMutation,
  usePatchOrgProductsMutation,
  useGetMyProductLicensesQuery,
  useGetOrgProductAccessQuery,
  useGetOrgAiUsageQuery,
  usePatchOrgProductAccessMutation,
  useGetWorkspaceContextQuery,
  usePatchWorkspaceDefaultsMutation,
  useDiscoverTenantForLoginMutation,
  useGetCostEventsQuery,
  useGetCostSummaryQuery,
  useSubmitWaitlistMutation,
  useGetWaitlistEntriesQuery,
  useGetWaitlistStatsQuery,
  useApproveWaitlistMutation,
  useRejectWaitlistMutation,
  useUpdateWaitlistNotesMutation,
  useResendWaitlistInviteMutation,
  useValidateInviteCodeQuery,
  useSearchQuery,
  useGetCanvasesQuery,
  useGetCanvasByIdQuery,
  useGetSharedCanvasQuery,
  useCreateCanvasMutation,
  useUpdateCanvasMutation,
  useDeleteCanvasMutation,
  useShareCanvasMutation,
  useUnshareCanvasMutation,
  useGetDependenciesQuery,
  useCreateDependencyMutation,
  useDeleteDependencyMutation,
  useGetMilestonesQuery,
  useGetMilestoneByIdQuery,
  useCreateMilestoneMutation,
  useUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
  useGetFollowUpsQuery,
  useParseFollowUpMutation,
  useCreateFollowUpMutation,
  useCompleteFollowUpMutation,
  usePostponeFollowUpMutation,
  useDeleteFollowUpMutation,
  useBulkUpdateTasksMutation,
  useBulkDeleteTasksMutation,
  useGetLeadCycleMetricsQuery,
  useGetWikiSpaceForProjectQuery,
  useGetWikiPagesQuery,
  useGetWikiTreeQuery,
  useGetWikiFreshnessQuery,
  useGetWikiRevisionsQuery,
  useGetWikiPageQuery,
  useGetWikiPageWatchersQuery,
  useToggleWikiPageWatchMutation,
  useCreateWikiPageMutation,
  useUpdateWikiPageMutation,
  useArchiveWikiPageMutation,
  useRevertWikiRevisionMutation,
  useWikiSummarizePageMutation,
  useWikiAiDraftMutation,
  useWikiExportMutation,
  useUploadWikiAttachmentMutation,
  useAskWikiQuestionMutation,
  useUpdateLocalePreferencesMutation,
} = apiSlice;

// Export the apiSlice for direct endpoint access
export const api = apiSlice;


